import React from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import PhotoAlbum from 'react-photo-album';
import { Link } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { Container, Jumbotron } from 'reactstrap';

import DownloadButton from '../Common/DownloadButton';

import { getSetting } from '../../../../helpers/Settings';
import { getCollectionPlaceholderElement } from '../../../../helpers/PageSections';

const imgStyle = {
  transition: 'transform .135s cubic-bezier(0.0,0.0,0.2,1),opacity linear .15s',
};

const cont = {
  backgroundColor: '#eee',
  cursor: 'pointer',
  overflow: 'hidden',
  position: 'relative',
  width: '100%',
};

const gridImageRenderer = ({
  photo,
  wrapperStyle,
  imageProps: {
    alt,
    style,
    ...restImageProps
  },
}) => {
  if (photo.isPlaceholder) {
    return (
      <div
        className="landing-gallery-photo-link"
        style={{
          ...cont,
          ...wrapperStyle,
          boxSizing: 'border-box',
          paddingBottom: 10,
          paddingTop: 10,
        }}
        aria-hidden="true"
      >
        {getCollectionPlaceholderElement({
          className: 'landing-gallery-photo-placeholder',
        })}
      </div>
    );
  }

  const LinkTag = photo.customUrl ? 'a' : Link;

  return (
    <LinkTag
      to={LinkTag === 'a' ? undefined : photo.targeturl}
      href={LinkTag === 'a' ? photo.targeturl : undefined}
      className="landing-gallery-photo-link"
      style={{
        ...cont,
        ...wrapperStyle,
        boxSizing: 'border-box',
        paddingBottom: 10,
        paddingTop: 10,
      }}
    >
      {photo.src ? (
        <img
          alt={alt || photo.title}
          src={photo.src}
          {...restImageProps}
          style={{
            display: 'block',
            height: '100%',
            objectFit: 'cover',
            width: style?.width || wrapperStyle?.width,
            ...imgStyle,
          }}
        />
      ) : (
        getCollectionPlaceholderElement({
          className: 'landing-gallery-photo-placeholder',
        })
      )}
      <div className="photo-gallery-content">
        <div className="photo-gallery-qty">
          <i className="fas fa-clone" />
        </div>
        <div className="photo-gallery-header">
          <div>
            {photo.subtitle && (
              <span className="photo-gallery-date">{photo.subtitle}</span>
            )}
            {photo.title && <h3>{photo.title}</h3>}
          </div>
          {photo.allowDownload && (
            <div>
              <DownloadButton collectionUuid={photo.collectionUuid} />
            </div>
          )}
        </div>
      </div>
    </LinkTag>
  );
};

export const PuckCollectionGridView = ({ photos, columns }) => (
  <PhotoAlbum
    layout="masonry"
    photos={photos}
    renderPhoto={gridImageRenderer}
    targetRowHeight={400}
    rowConstraints={{ singleRowMaxHeight: 400 }}
    componentsProps={{
      columnContainerProps: { className: 'landing-gallery-column-container' },
    }}
    columns={(containerWidth) => {
      if (columns === 1) return 1;
      if (containerWidth < 320) return 1;
      if (containerWidth < 600) return 2;
      return columns || 3;
    }}
  />
);

PuckCollectionGridView.propTypes = {
  photos: PropTypes.array.isRequired,
  columns: PropTypes.number,
};

PuckCollectionGridView.defaultProps = {
  columns: null,
};

export const PuckCollectionFeaturedView = ({ collections, allowDownload }) => (
  <div className="custom-page-section custom-page-section-no-padding landing-featured-images">
    <Container fluid>
      <div className="landing-featured-images-wrapper">
        {collections.map((collection) => {
          const { isPlaceholder } = collection;
          const LinkTag = collection.customUrl ? 'a' : Link;
          const targetUrl = collection.targeturl || `/collections/${collection.uuid}`;
          const featuredImageSrc =
            collection.featured_file?.urls?.preview_1920
            || collection.featured_file?.urls?.original
            || collection.featured_file?.urls?.featured
            || collection.featured_file?.urls?.thumbnailx2
            || collection.featured_file?.urls?.thumbnailmasonry
            || collection.featured_file?.urls?.thumbnail
            || null;

          if (isPlaceholder || !featuredImageSrc) {
            return (
              <div
                key={collection.id}
                className={classnames(
                  'landing-featured-images-item',
                  `item${Math.min(collections.length, 4)}`,
                )}
                aria-hidden="true"
              >
                {getCollectionPlaceholderElement({ className: 'landing-featured-images-bg' })}
                <div className={classnames('featured-image-header', {
                  'featured-image-header-red': getSetting('cs1'),
                })}
                >
                  {collection.title && <h3>{collection.title}</h3>}
                  {collection.subtitle && <h4>{collection.subtitle}</h4>}
                </div>
              </div>
            );
          }

          return (
            <LinkTag
              key={collection.uuid}
              to={LinkTag === 'a' ? undefined : targetUrl}
              href={LinkTag === 'a' ? targetUrl : undefined}
              className={classnames(
                'landing-featured-images-item',
                `item${Math.min(collections.length, 4)}`,
              )}
            >
              <img
                src={featuredImageSrc}
                alt=""
                aria-hidden="true"
                className="landing-featured-images-bg"
              />
              <div className={classnames('featured-image-header', {
                'featured-image-header-red': getSetting('cs1'),
              })}
              >
                <div>
                  <div>
                    {collection.title && <h3>{collection.title}</h3>}
                    {collection.subtitle && <h4>{collection.subtitle}</h4>}
                  </div>
                  {allowDownload && (
                    <div>
                      <DownloadButton collectionUuid={collection.uuid} />
                    </div>
                  )}
                </div>
              </div>
            </LinkTag>
          );
        })}
      </div>
    </Container>
  </div>
);

PuckCollectionFeaturedView.propTypes = {
  collections: PropTypes.array.isRequired,
  allowDownload: PropTypes.bool,
};

PuckCollectionFeaturedView.defaultProps = {
  allowDownload: false,
};

export const PuckCollectionJumbotronView = ({ collections }) => {
  const intl = useIntl();

  return (
    <>
      {collections.map((collection) => {
        const { isPlaceholder } = collection;
        const LinkTag = collection.customUrl ? 'a' : Link;
        const targetUrl = collection.targeturl || `/collections/${collection.uuid}`;
        let featuredImageSrc =
          collections.length === 1 ?
            collection.featured_file?.urls?.preview_1920 :
            collection.featured_file?.urls?.preview_1280;
        featuredImageSrc = featuredImageSrc
          || collection.featured_file?.urls?.thumbnailx2
          || collection.featured_file?.urls?.thumbnailmasonry
          || collection.featured_file?.urls?.thumbnail
          || null;

        if (isPlaceholder || !featuredImageSrc) {
          return (
            <Jumbotron
              key={collection.id}
              className="custom-page-section featured-jumbotron"
              fluid
            >
              {getCollectionPlaceholderElement({ className: 'featured-jumbotron-bg' })}
              <Container>
                <div className={classnames('jumbotron-content', { 'jumbotron-content-red': getSetting('cs1') })}>
                  <h1>{collection.title}</h1>
                  <p>{collection.subtitle || intl.formatMessage({ defaultMessage: 'View Photos' })}</p>
                </div>
              </Container>
            </Jumbotron>
          );
        }

        return (
          <LinkTag
            key={collection.uuid}
            to={LinkTag === 'a' ? undefined : targetUrl}
            href={LinkTag === 'a' ? targetUrl : undefined}
            className="featured-jumbotron-link"
          >
            <Jumbotron
              className="custom-page-section featured-jumbotron"
              fluid
            >
              <img
                src={featuredImageSrc}
                alt=""
                aria-hidden="true"
                className="featured-jumbotron-bg"
              />
              <Container>
                <div className={classnames('jumbotron-content', { 'jumbotron-content-red': getSetting('cs1') })}>
                  <h1>{collection.title}</h1>
                  <p>{collection.subtitle || intl.formatMessage({ defaultMessage: 'View Photos' })}</p>
                </div>
              </Container>
            </Jumbotron>
          </LinkTag>
        );
      })}
    </>
  );
};

PuckCollectionJumbotronView.propTypes = {
  collections: PropTypes.array.isRequired,
};
